import axios from 'axios';
import { createContext, useContext, useEffect, useState } from 'react';

import { COOKIE_CONFIG, SHORT_COOKIE_OPTIONS } from '@/config/cookie.config.js';
import { SERVER_URL } from '@/constants/index.js';
import { clearCookies, getCookie, setCookie } from '@/lib/cookies.js';
import {
  auth,
  signInWithGoogle as firebaseSignInWithGoogle,
  signOut as firebaseSignOut,
  onAuthStateChange,
} from '@/lib/firebase';
import { notifications } from '@/lib/notifications';
import { queryClient } from '@/QueryClient.js';

/**
 * @typedef {Object} User
 * @property {string} uid - Firebase User ID
 * @property {string} email - User email
 * @property {string} displayName - User display name
 * @property {string} photoURL - User photo URL
 * @property {string} [id] - Database user ID
 * @property {string} [role] - User role (admin, inspector, manager)
 * @property {string} [status] - User status (active, inactive, suspended)
 * @property {Object} [profile] - Additional profile data from server
 */

/**
 * @typedef {Object} AuthContextType
 * @property {User|null} user - Current authenticated user
 * @property {boolean} loading - Loading state
 * @property {boolean} isAuthenticated - Authentication status
 * @property {function(): Promise<void>} signInWithGoogle - Google sign in function
 * @property {function(): Promise<void>} signOut - Sign out function
 * @property {string|null} error - Error message
 * @property {function(): Promise<void>} refreshUser - Refresh user data from server
 */

/**
 * Authentication context
 * @type {React.Context<AuthContextType|undefined>}
 */
const AuthContext = createContext(undefined);

/**
 * Custom hook to use authentication context
 * @returns {AuthContextType} Authentication context
 * @throws {Error} When used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication provider component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} AuthProvider component
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Store user data in secure cookies
   * @param {Object} userData - User data to store
   * @param {string} idToken - Firebase ID token
   */
  const storeUserData = (userData, idToken) => {
    try {
      setCookie(COOKIE_CONFIG.AUTH_TOKEN, idToken, SHORT_COOKIE_OPTIONS);
    } catch (error) {
      console.error('Failed to store user data in cookies:', error);
      notifications.showError({
        description: 'Failed to save session data',
      });
    }
  };

  /**
   * Retrieve user data from cookies
   * @returns {Object|null} User data or null
   */
  const getUserDataFromCookies = () => {
    try {
      const userDataString = getCookie(COOKIE_CONFIG.USER_DATA);
      const isAuthenticated =
        getCookie(COOKIE_CONFIG.IS_AUTHENTICATED) === 'true';

      if (userDataString && isAuthenticated) {
        return JSON.parse(userDataString);
      }
    } catch (error) {
      console.error('Failed to parse user data from cookies:', error);
      clearCookies();
    }
    return null;
  };

  /**
   * Get stored auth token
   * @returns {string|null} Auth token or null
   */
  const getStoredAuthToken = () => {
    return getCookie(COOKIE_CONFIG.AUTH_TOKEN);
  };

  /**
   * Clear all stored user data
   */
  const clearStoredUserData = () => {
    clearCookies();
  };

  /**
   * Sync user data with server after Firebase authentication
   * @param {string} idToken - Firebase ID token
   * @returns {Promise<Object>} Server user data
   */
  const syncUser = async (idToken) => {
    try {
      const config = {
        method: 'POST',
        url: `${SERVER_URL}/auth/sync`,
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      };

      const response = await axios(config);

      if (response.status !== 200) {
        // Clear any stored auth tokens
        clearStoredUserData();
        throw new Error('Failed to sync user with server');
      }

      return response.data.data;
    } catch (error) {
      console.error('User sync failed:', error);
      if (error.response?.data?.message) {
        notifications.showError({
          description: error.response?.data?.message,
        });
      } else {
        notifications.showError({
          description: 'Failed to sync user with server',
        });
      }
      signOut(); // Sign out on sync failure
      return null;
    }
  };

  /**
   * Refresh user data from server
   * @returns {Promise<void>}
   */
  const refreshUser = async () => {
    if (!user) return;

    try {
      setError(null);
      const firebaseUser = auth.currentUser;
      if (firebaseUser) {
        const idToken = await firebaseUser.getIdToken(true); // Force refresh
        const serverUser = await syncUser(idToken);
        if (serverUser) {
          const updatedUser = {
            ...user,
            ...serverUser,
            uid: firebaseUser.uid, // Ensure Firebase UID is preserved
          };
          setUser(updatedUser);
          storeUserData(updatedUser, idToken);
        }
      }
    } catch (error) {
      setError('Failed to refresh user data');
      console.error('User refresh failed:', error);
    }
  };

  /**
   * Handle Google sign in with server sync
   * @returns {Promise<void>}
   */
  const signInWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);

      // Sign in with Firebase
      const result = await firebaseSignInWithGoogle();
      const firebaseUser = result.user;

      // Check email domain
      if (firebaseUser.email) {
        const emailDomain = firebaseUser.email.split('@')[1];
        if (
          !import.meta.env.VITE_APP_INSPECTOR_EMAIL_DOMAINS.split(',').includes(
            emailDomain
          )
        ) {
          throw error;
        }
      }
    } catch (error) {
      setError(error.message);
      console.error('Sign-in failed:', error);
      notifications.showError({
        description: error.message
          ? error.message
          : 'Sign-in failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle sign out
   * @returns {Promise<void>}
   */
  const signOut = async () => {
    try {
      setError(null);
      setLoading(true);

      // Sign out from Firebase
      await firebaseSignOut();

      // Clear stored data
      clearStoredUserData();
      queryClient.clear();

      setUser(null);
    } catch (error) {
      setError(error.message);
      console.error('Sign out failed:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if stored token is still valid
   * @param {string} token - Token to validate
   * @returns {Promise<boolean>} Whether token is valid
   */
  const validateStoredToken = async (token) => {
    try {
      const response = await axios({
        method: 'GET',
        url: `${SERVER_URL}/auth/validate`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.status === 200;
    } catch (error) {
      console.warn('Token validation failed:', error);
      return false;
    }
  };

  // Listen to Firebase authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (firebaseUser) => {
      setLoading(true);

      if (firebaseUser) {
        try {
          // Get fresh ID token
          const idToken = await firebaseUser.getIdToken();

          // Sync with server
          const serverUser = await syncUser(idToken);

          // Combine Firebase and server data
          const userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            ...serverUser,
          };

          setUser(userData);
          storeUserData(userData, idToken);
        } catch (error) {
          console.error('Failed to sync user on auth state change:', error);
          setError('Failed to sync with server');
          // Still set basic Firebase user data
          const basicUserData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
          };
          setUser(basicUserData);
          storeUserData(basicUserData, await firebaseUser.getIdToken());
        }
      } else {
        // User is signed out
        setUser(null);
        clearStoredUserData();
      }

      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Check for persisted auth state on initial load
  // ? DO WE NEED THIS ?
  // useEffect(() => {
  //   const checkPersistedAuth = async () => {
  //     const savedUser = getUserDataFromCookies();
  //     const isAuthenticated =
  //       getCookie(COOKIE_CONFIG.IS_AUTHENTICATED) === 'true';
  //     const savedToken = getStoredAuthToken();

  //     if (savedUser && isAuthenticated && savedToken) {
  //       try {
  //         // Validate the stored token
  //         const isValidToken = await validateStoredToken(savedToken);

  //         if (isValidToken) {
  //           // Try to get fresh data from Firebase
  //           const firebaseUser = auth.currentUser;
  //           if (firebaseUser) {
  //             try {
  //               const idToken = await firebaseUser.getIdToken(true);
  //               const serverUser = await syncUser(idToken);

  //               if (serverUser) {
  //                 const updatedUser = {
  //                   ...savedUser,
  //                   ...serverUser,
  //                   uid: firebaseUser.uid,
  //                 };
  //                 setUser(updatedUser);
  //                 storeUserData(updatedUser, idToken);
  //               } else {
  //                 setUser(savedUser);
  //               }
  //             } catch (error) {
  //               console.error('Failed to refresh token on init:', error);
  //               setUser(savedUser); // Use cached user data
  //             }
  //           } else {
  //             setUser(savedUser); // Use cached user data
  //           }
  //         } else {
  //           // Token is invalid, clear everything
  //           console.warn('Stored token is invalid, clearing auth data');
  //           clearStoredUserData();
  //           setUser(null);
  //         }
  //       } catch (error) {
  //         console.error('Failed to validate stored auth:', error);
  //         clearStoredUserData();
  //         setUser(null);
  //       }
  //     }

  //     setLoading(false);
  //   };

  //   checkPersistedAuth();
  // }, []);

  // Auto-refresh token before expiration
  useEffect(() => {
    if (!user || !auth.currentUser) return;

    const refreshInterval = setInterval(async () => {
      try {
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
          const idToken = await firebaseUser.getIdToken(true);
          setCookie(COOKIE_CONFIG.AUTH_TOKEN, idToken, SHORT_COOKIE_OPTIONS);
        }
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }, 50 * 60 * 1000); // Refresh every 50 minutes (Firebase tokens expire in 1 hour)

    return () => clearInterval(refreshInterval);
  }, [user]);

  /**
   * @type {AuthContextType}
   */
  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    signInWithGoogle,
    signOut,
    error,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
